import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Bell, Users, FileText, CreditCard, Clock, CheckCircle2, Bot, AlertCircle } from 'lucide-react';
import { demoNotifications } from '../data/dummyData';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const typeIcon = {
  team: Users,
  approval: CheckCircle2,
  file: FileText,
  deadline: Clock,
  contract: FileText,
  payment: CreditCard,
  ai: Bot,
  task: CheckCircle2,
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState(demoNotifications);

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="notifications-page">
      <motion.div variants={fadeUp} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-1">{unreadCount} unread</p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={markAllRead} data-testid="mark-all-read-btn">
            Mark all read
          </Button>
        )}
      </motion.div>

      <motion.div variants={fadeUp} className="space-y-2">
        {notifications.map(n => {
          const Icon = typeIcon[n.type] || Bell;
          return (
            <div key={n.id} className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${n.read ? 'border-border/30 bg-card' : 'border-border/50 bg-card'}`} data-testid={`notification-${n.id}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${n.read ? 'bg-secondary' : 'bg-foreground'}`}>
                <Icon className={`w-4 h-4 ${n.read ? 'text-muted-foreground' : 'text-background'}`} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${n.read ? 'text-muted-foreground' : 'font-medium'}`}>{n.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{n.description}</p>
                <p className="text-[10px] text-muted-foreground/60 mt-1">{n.time}</p>
              </div>
              {!n.read && <div className="w-2 h-2 rounded-full bg-foreground flex-shrink-0 mt-1.5" />}
            </div>
          );
        })}
      </motion.div>

      {notifications.length === 0 && (
        <div className="text-center py-16">
          <Bell className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" strokeWidth={2} />
          <p className="text-sm text-muted-foreground">No notifications</p>
        </div>
      )}
    </motion.div>
  );
}
